import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { studentsAPI } from '../../services/api';
import { Spinner, Button } from '../../components/ui';
import toast from 'react-hot-toast';
import WhatsAppModal from '../../components/WhatsAppModal';

const STATUS_OPTIONS = ['Registered', 'Contacted', 'Demo Scheduled', 'Demo Done', 'Enrolled', 'Dropped'];
const STATUS_STYLE = {
  Registered:       { bg: '#dbeafe', color: '#1d4ed8' },
  Contacted:        { bg: '#ede9fe', color: '#7c3aed' },
  'Demo Scheduled': { bg: '#fef9c3', color: '#a16207' },
  'Demo Done':      { bg: '#e0f2fe', color: '#0369a1' },
  Enrolled:         { bg: '#dcfce7', color: '#15803d' },
  Dropped:          { bg: '#fee2e2', color: '#dc2626' },
};

export default function StudentDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [waModal, setWaModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notes, setNotes] = useState('');
  const [status, setStatus] = useState('');
  const [scholarshipTaken, setScholarshipTaken] = useState(false);
  const [scholarshipScore, setScholarshipScore] = useState('');

  useEffect(() => {
    studentsAPI.getById(id)
      .then(r => {
        const s = r.data?.data;
        setStudent(s);
        setNotes(s?.notes || '');
        setStatus(s?.status || 'Registered');
        setScholarshipTaken(s?.scholarship_test_taken || false);
        setScholarshipScore(s?.scholarship_score || '');
      })
      .catch(() => { toast.error('Student not found'); navigate('/students'); })
      .finally(() => setLoading(false));
  }, [id]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await studentsAPI.update(id, {
        status, notes,
        scholarship_test_taken: scholarshipTaken,
        scholarship_score: scholarshipScore ? Number(scholarshipScore) : undefined,
      });
      setStudent(res.data?.data);
      toast.success('Saved');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally { setSaving(false); }
  };

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><Spinner /></div>;
  if (!student) return null;

  const ss = STATUS_STYLE[status] || STATUS_STYLE.Registered;
  const roi = student.current_salary_expectation && student.desired_salary_after_program
    ? student.desired_salary_after_program - student.current_salary_expectation
    : null;

  return (
    <>
    <div style={{ maxWidth: 700, margin: '0 auto' }}>
      {/* Back */}
      <button onClick={() => navigate('/students')}
        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', fontSize: 13, padding: '0 0 12px 0' }}>
        ← Students
      </button>

      {/* Header */}
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14, padding: '20px 22px', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ width: 52, height: 52, borderRadius: '50%', background: 'linear-gradient(135deg, #1B2A6B, #2d3f8a)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 800, fontSize: 20 }}>
              {student.name.charAt(0).toUpperCase()}
            </div>
            <div>
              <h1 style={{ fontSize: 18, fontWeight: 800, color: '#1B2A6B', margin: 0 }}>{student.name}</h1>
              <p style={{ fontSize: 13, color: '#64748b', margin: '2px 0 0' }}>
                {student.phone && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    {student.phone}
                    <button onClick={() => setWaModal(true)} title="Send WhatsApp"
                      style={{ background: '#25d366', border: 'none', borderRadius: 5, color: '#fff', cursor: 'pointer', fontSize: 11, padding: '1px 7px', lineHeight: 1.5 }}>
                      💬 WA
                    </button>
                  </span>
                )}
                {student.email && ` · ${student.email}`}
              </p>
            </div>
          </div>
          <span style={{ fontSize: 12, padding: '4px 12px', borderRadius: 12, fontWeight: 700, background: ss.bg, color: ss.color }}>
            {status}
          </span>
        </div>

        {/* Info Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginTop: 16 }}>
          {[
            { label: 'College', value: student.college || '—' },
            { label: 'Course', value: student.course || '—' },
            { label: 'Grad Year', value: student.graduation_year || '—' },
            { label: 'City', value: student.city || '—' },
            { label: 'Interest', value: student.interest_area || '—' },
            { label: 'Source', value: student.registration_source?.replace('_', ' ') || '—' },
          ].map(({ label, value }) => (
            <div key={label} style={{ background: '#f8fafc', borderRadius: 8, padding: '8px 10px' }}>
              <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
              <div style={{ fontSize: 13, color: '#1e293b', fontWeight: 600, marginTop: 2 }}>{value}</div>
            </div>
          ))}
        </div>

        {/* ROI Widget */}
        {(student.current_salary_expectation || student.desired_salary_after_program) && (
          <div style={{ marginTop: 14, background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '12px 14px' }}>
            <p style={{ fontSize: 11, fontWeight: 700, color: '#15803d', margin: '0 0 8px' }}>📈 ROI ESTIMATE</p>
            <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
              {student.current_salary_expectation && (
                <div>
                  <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 700 }}>CURRENT EXPECTATION</div>
                  <div style={{ fontSize: 16, fontWeight: 800, color: '#334155' }}>₹{student.current_salary_expectation} LPA</div>
                </div>
              )}
              {student.desired_salary_after_program && (
                <div>
                  <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 700 }}>TARGET AFTER PROGRAM</div>
                  <div style={{ fontSize: 16, fontWeight: 800, color: '#15803d' }}>₹{student.desired_salary_after_program} LPA</div>
                </div>
              )}
              {roi !== null && (
                <div>
                  <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 700 }}>EXPECTED UPLIFT</div>
                  <div style={{ fontSize: 16, fontWeight: 800, color: '#C9A84C' }}>+₹{roi} LPA</div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Partner */}
        {student.partner_id && (
          <div style={{ marginTop: 12, fontSize: 12, color: '#64748b' }}>
            Referred by <strong>{student.partner_id.name}</strong> · {student.partner_id.email}
          </div>
        )}
        {student.lead_id && (
          <div style={{ marginTop: 6 }}>
            <button onClick={() => navigate(`/leads/${student.lead_id._id || student.lead_id}`)}
              style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 12, color: '#2563eb', fontFamily: 'inherit', padding: 0 }}>
              → View Associated Lead ({student.lead_id.lead_id || 'Lead'})
            </button>
          </div>
        )}
      </div>

      {/* Edit Panel */}
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14, padding: '20px 22px' }}>
        <h2 style={{ fontSize: 14, fontWeight: 700, color: '#1B2A6B', margin: '0 0 16px' }}>Update Status & Notes</h2>

        {/* Status */}
        <div style={{ marginBottom: 14 }}>
          <label style={{ fontSize: 12, fontWeight: 700, color: '#475569', display: 'block', marginBottom: 6 }}>Status</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {STATUS_OPTIONS.map(s => {
              const st = STATUS_STYLE[s];
              return (
                <button key={s} onClick={() => setStatus(s)}
                  style={{
                    padding: '6px 14px', borderRadius: 20, border: '1.5px solid',
                    fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s',
                    background: status === s ? st.color : st.bg,
                    color: status === s ? '#fff' : st.color,
                    borderColor: st.color,
                  }}>
                  {s}
                </button>
              );
            })}
          </div>
        </div>

        {/* Scholarship */}
        <div style={{ marginBottom: 14, display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#475569' }}>
            <input type="checkbox" checked={scholarshipTaken} onChange={e => setScholarshipTaken(e.target.checked)}
              style={{ width: 16, height: 16 }} />
            🎓 Scholarship Test Taken
          </label>
          {scholarshipTaken && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <label style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>Score:</label>
              <input type="number" min={0} max={100} value={scholarshipScore}
                onChange={e => setScholarshipScore(e.target.value)}
                placeholder="e.g. 78"
                style={{ width: 80, padding: '6px 10px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', outline: 'none' }} />
            </div>
          )}
        </div>

        {/* Notes */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 12, fontWeight: 700, color: '#475569', display: 'block', marginBottom: 6 }}>Notes</label>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={4}
            placeholder="Add notes about this student, follow-up actions, etc."
            style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #e2e8f0', borderRadius: 10, fontSize: 13, fontFamily: 'inherit', resize: 'vertical', outline: 'none', boxSizing: 'border-box' }} />
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="ghost" size="sm" onClick={() => navigate('/students')}>Cancel</Button>
          <Button variant="primary" size="sm" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save Changes'}
          </Button>
        </div>
      </div>
    </div>
    {waModal && (
      <WhatsAppModal
        targetId={id}
        targetType="student"
        phone={student.phone}
        recipientName={student.name}
        onClose={() => setWaModal(false)}
      />
    )}
    </>
  );
}
